import { useCallback, useEffect } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking';
import { PlaylistCreatedAndAssignedByLabel } from '../../savedItems';
import { useMyPlaylistsFilters, useMyPlaylistsParams } from '../myPlaylistsHooks';
import { useMyPlaylists } from '../myPlaylistsQueries';
import { MY_PLAYLISTS_SORT_MAP, MY_PLAYLISTS_TABS_MAP_TO_INTERACTION } from '../myPlaylistsTypes';
import { useDisplayedData } from '../../table/tableHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { useHasScope } from '../../accounts';
import { RouterLink } from '../../../components/AcademyLinks';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LearningContentCard } from '../../cards/components/LearningContentCard';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { View } from 'foundations-components/transitional/components/View';
import styledWithTheme from 'foundations-theming/utils';
import { PageHeader } from '../../header/components/PageHeader';
import { FiltersCard } from '../../filters/components/FiltersCard';
import { PageResultsCount } from '../../header/components/PageResultsCount';
import { PAGE_SIZE } from '../../pagination/paginationConsts';
import { FilterTags } from '../../filters/components/FilterTags';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import ApiError from '../../../components/Errors/ApiError';
import { NavMarker } from 'react-rhumb';
import BlankPageMediaWrapper from '../../../components/BlankPageMediaWrapper';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import NoResultsError from '../../../components/Errors/NoResultsError';
import { useLocation } from 'react-router-dom';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import I18n from 'I18n';
import { SearchFilter } from '../../filters/components/SearchFilter';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import PaginatedUICardGrid from '../../../containers/MyLearningPage/PaginatedUICardGrid';
import { safeSmoothScrollTo } from '../../../utils/utils';
import { usePagination } from '../../pagination/paginationHooks';
import { MyPlaylistsButtonGroupFilter } from '../../filters/components/MyPlaylistsButtonGroupFilter';
import { SortByAccess } from '../../filters/components/SortByAccess';
import { SortByRelevance } from '../../filters/components/SortByRelevance';
import { Text } from 'foundations-components/components/Text';
import { MyPlaylistsDropdownFilter } from '../../filters/components/MyPlaylistsDropdownFilter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useMyPlaylistsTab = () => {
  const {
    tab
  } = useMyPlaylistsParams();
  const {
    searchFilter,
    sortByAll: sortBy
  } = useMyPlaylistsFilters();
  const {
    currentPage: page
  } = usePagination();
  const hasFilters = searchFilter === null || searchFilter === void 0 ? void 0 : searchFilter.length;
  const {
    sortProperty,
    sortOrder
  } = MY_PLAYLISTS_SORT_MAP[sortBy || (searchFilter ? 'RELEVANCE_DESCENDING' : 'RECENTLY_ACCESSED_DESCENDING')];
  const {
    totalPlaylists,
    playlists,
    playlistsLoading,
    playlistsError
  } = useMyPlaylists({
    interactionTypes: MY_PLAYLISTS_TABS_MAP_TO_INTERACTION[tab || 'all'],
    limit: PAGE_SIZE,
    page,
    sortOrder,
    sortProperty,
    query: searchFilter
  });
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: (playlists === null || playlists === void 0 ? void 0 : playlists.map(({
      id,
      type,
      language,
      hiddenForOwner
    }) => ({
      id,
      type: type === 'LEARNING_PATH' ? 'LEARNING_PATH' : 'PLAYLIST',
      language,
      hiddenForOwner
    }))) || [],
    skip: !playlists
  });
  return {
    totalPlaylists,
    page,
    playlists: cardModels,
    playlistsLoading: playlistsLoading || cardModelsLoading || userProgressDataLoading,
    playlistsError: playlistsError || cardModelsError,
    hasFilters,
    searchFilter,
    sortBy,
    tab
  };
};
const ArchivedMessageWrapper = styledWithTheme.span(({
  theme
}) => ({
  '&::before': {
    content: '('
  },
  '&::after': {
    content: ')'
  },
  textTransform: 'none',
  marginBlockStart: theme.space['100'],
  marginInlineStart: theme.space['100'],
  opacity: 0.5,
  color: theme.color['text-core-on-fill-default']
}));
const HubspotPlaylistsAdminLink = () => {
  return /*#__PURE__*/_jsxs(RouterLink, {
    to: '/hubspot-playlists-admin',
    children: [/*#__PURE__*/_jsx(HelpIcon, {
      marginInlineEnd: "100",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlists.hubspotPlaylists.admin.subtitle"
      }),
      tooltipPlacement: "left"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.playlists.hubspotPlaylists.admin.heading"
    })]
  });
};
const ItemRightComponent = props => {
  return /*#__PURE__*/_jsx(Illustration, Object.assign({
    inlineSize: "100%",
    name: "training-academy"
  }, props));
};
const MyPlaylistsNoResults = ({
  tab
}) => {
  const {
    isMobile
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MY_PLAYLISTS_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      marginBlockStart: isMobile ? '600' : '600',
      children: /*#__PURE__*/_jsxs(BlankPageMediaWrapper, {
        "data-test-id": "my-playlists-empty-state",
        itemRightComponent: ItemRightComponent,
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `learningCenter.myPlaylists.tab.${tab}.blankHeader`
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `learningCenter.myPlaylists.tab.${tab}.blankBody`
          })
        }), /*#__PURE__*/_jsx(RouterButton, {
          to: "/",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.myLearning.emptyState.goBack"
          })
        })]
      })
    })
  });
};
const MyPlaylistsNoResultsWithFilter = () => {
  const location = useLocation();
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MY_PLAYLISTS_LISTING_NO_RESULTS",
    children: /*#__PURE__*/_jsx(NoResultsError, {
      resetLink: location.pathname
    })
  });
};
const MyPlaylistsContent = ({
  playlistsToDisplay,
  playlists,
  tab,
  totalCount
}) => {
  const {
    currentPage,
    setPage
  } = usePagination();
  const isGhost = useCallback(playlistId => playlists !== null && !playlists.some(p => p.id === playlistId), [playlists]);
  const isArchived = useCallback(playlistId => tab !== 'archived' && playlists !== null && playlists.some(p => p.id === playlistId && p.hiddenForOwner), [playlists, tab]);
  const handlePageChange = pageNumber => {
    setPage(pageNumber);
    safeSmoothScrollTo(window, 0, 0);
  };
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MYLEARNING_LISTING_LOADED",
    children: /*#__PURE__*/_jsx(PaginatedUICardGrid, {
      isLoadingPageOnDemand: true,
      limit: PAGE_SIZE,
      onPageChange: handlePageChange,
      page: currentPage,
      totalCount: totalCount,
      children: playlistsToDisplay === null || playlistsToDisplay === void 0 ? void 0 : playlistsToDisplay.map(playlist => {
        const {
          id,
          description
        } = playlist;
        return /*#__PURE__*/_jsx(LearningContentCard, {
          body: /*#__PURE__*/_jsxs(_Fragment, {
            children: [tab !== 'created-by-me' && tab !== 'archived' && /*#__PURE__*/_jsx("div", {
              style: {
                opacity: isArchived(id) ? 0.5 : 1
              },
              children: /*#__PURE__*/_jsx(PlaylistCreatedAndAssignedByLabel, {
                marginBlockEnd: "200",
                playlistId: id
              })
            }), /*#__PURE__*/_jsx(TruncateMultiline, {
              maxChars: 150,
              text: description || ''
            })]
          }),
          isGhost: isGhost(id) || isArchived(id),
          item: playlist,
          overviewType: "my-playlists",
          renderThumbnailContentTypeName: type => /*#__PURE__*/_jsxs(_Fragment, {
            children: [type, isArchived(id) && /*#__PURE__*/_jsx(ArchivedMessageWrapper, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myPlaylists.archived"
              })
            })]
          })
        }, id);
      })
    })
  });
};
export const MyPlaylistsTabs = () => {
  const {
    hasFilters,
    playlists,
    page,
    totalPlaylists,
    playlistsLoading,
    playlistsError,
    searchFilter,
    sortBy,
    tab
  } = useMyPlaylistsTab();
  const {
    isMobile,
    isTablet
  } = useMediaQuery();

  // displays only initial data dependent on tab, page and filters in order
  // to render ghosts
  const displayedData = useDisplayedData({
    playlists,
    totalPlaylists
  }, {
    loading: playlistsLoading,
    initialOnly: true,
    parameters: {
      tab,
      page,
      searchFilter,
      sortBy
    }
  });
  useEffect(() => {
    UsageTracker.trackStandalone('Pageview', {
      screen: SCREENS.MY_PLAYLISTS
    });
  }, []);
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  const isRequesting = playlistsLoading && !displayedData;
  const {
    playlists: playlistsToDisplay,
    totalPlaylists: displayedTotalPlaylists
  } = displayedData || {};
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PageHeader, {
      subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingSubTitles.MY_PLAYLISTS"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.heading.headingTitles.MY_PLAYLISTS"
      })
    }), !isMobile && /*#__PURE__*/_jsx(FiltersCard, {
      marginBlockEnd: "600",
      rightSlot: /*#__PURE__*/_jsxs(TFlex, {
        alignItems: "center",
        gap: 8,
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.topFilters.sortBy.filterTitle"
          })
        }), /*#__PURE__*/_jsx(SortByAccess, {
          appearsIn: "my-playlists"
        }), (searchFilter === null || searchFilter === void 0 ? void 0 : searchFilter.length) && /*#__PURE__*/_jsx(SortByRelevance, {
          appearsIn: "my-playlists"
        }), hasPublicPlaylistsAdminScope && /*#__PURE__*/_jsx(HubspotPlaylistsAdminLink, {})]
      }),
      children: isTablet ? /*#__PURE__*/_jsx(MyPlaylistsDropdownFilter, {
        appearsIn: "my-playlists"
      }) : /*#__PURE__*/_jsx(MyPlaylistsButtonGroupFilter, {
        appearsIn: "my-playlists"
      })
    }), /*#__PURE__*/_jsxs(TFlex, {
      alignItems: "center",
      gap: 16,
      marginBlockEnd: "600",
      children: [!isMobile && /*#__PURE__*/_jsx(Box, {
        flexGrow: 1,
        children: !isRequesting && /*#__PURE__*/_jsx(PageResultsCount, {
          page: page - 1,
          pageSize: PAGE_SIZE,
          total: displayedTotalPlaylists || 0
        })
      }), isMobile && /*#__PURE__*/_jsx(Box, {
        children: /*#__PURE__*/_jsx(MyPlaylistsDropdownFilter, {
          appearsIn: "my-playlists"
        })
      }), /*#__PURE__*/_jsx(Box, {
        flexGrow: isMobile ? 1 : 0,
        children: /*#__PURE__*/_jsx(SearchFilter, {
          appearsIn: "my-playlists",
          maxInlineSize: isMobile ? undefined : '320px',
          placeholder: I18n.text('learningCenter.myPlaylists.searchPlaceholder')
        })
      })]
    }), /*#__PURE__*/_jsx(FilterTags, {
      appearsIn: "my-playlists",
      marginBlockEnd: "500"
    }), /*#__PURE__*/_jsx(View, {
      margin: "-200",
      children: playlistsError ? /*#__PURE__*/_jsx(ApiError, {
        error: playlistsError,
        marker404: "MY_PLAYLISTS_LISTING_API_404",
        markerDefault: "MY_PLAYLISTS_LISTING_API_ERROR"
      }) : isRequesting ? /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 100
      }) : playlistsToDisplay !== null && playlistsToDisplay !== void 0 && playlistsToDisplay.length ? /*#__PURE__*/_jsx(MyPlaylistsContent, {
        playlists: playlists || [],
        playlistsToDisplay: playlistsToDisplay,
        tab: tab,
        totalCount: displayedTotalPlaylists || 0
      }) : hasFilters ? /*#__PURE__*/_jsx(MyPlaylistsNoResultsWithFilter, {}) : /*#__PURE__*/_jsx(MyPlaylistsNoResults, {
        tab: tab
      })
    })]
  });
};